import { useState, useEffect } from 'react';
import { catalogApi, type CatalogItemDto } from '../../api/catalog';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';

export default function CatalogList() {
  const { addToast } = useToast();
  const [items, setItems] = useState<CatalogItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItemDto | null>(null);

  const [form, setForm] = useState({
    name: '', unit: 'vnt.', unitPrice: 0, vatRate: 21,
    incomeAccount: 0, expenseAccount: 0, vatAccount: 0,
    type: 'service', description: '', code: ''
  });

  const loadItems = async () => {
    setLoading(true);
    try {
      const result = await catalogApi.list({ searchText: searchText || undefined, type: filterType || undefined });
      setItems(result);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { loadItems(); }, []);

  const handleSubmit = async () => {
    if (editingItem) {
      await catalogApi.update(editingItem.id, { ...form, isObsolete: false });
    } else {
      await catalogApi.create(form);
    }
    setShowAdd(false);
    setEditingItem(null);
    resetForm();
    loadItems();
  };

  const handleDelete = async (id: number) => {
    if (confirm('Delete this catalog item?')) {
      const item = items.find(i => i.id === id);
      await catalogApi.delete(id);
      addToast(`Catalog item "${item?.name || '#' + id}" deleted`, 'success');
      loadItems();
    }
  };

  const resetForm = () => setForm({ name: '', unit: 'vnt.', unitPrice: 0, vatRate: 21, incomeAccount: 0, expenseAccount: 0, vatAccount: 0, type: 'service', description: '', code: '' });

  const startEdit = (item: CatalogItemDto) => {
    setEditingItem(item);
    setForm({ name: item.name, unit: item.unit, unitPrice: item.unitPrice, vatRate: item.vatRate, incomeAccount: item.incomeAccount, expenseAccount: item.expenseAccount, vatAccount: item.vatAccount, type: item.type, description: item.description || '', code: item.code || '' });
    setShowAdd(true);
  };

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Services & Goods Catalog</h1>
        <button onClick={() => { resetForm(); setEditingItem(null); setShowAdd(!showAdd); }}>
          {showAdd ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      <div className="flex gap-3 items-end mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input className="px-2 py-1.5 text-sm border border-gray-300 rounded-md w-48" placeholder="Filter by name..." value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadItems()} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-md" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All types</option>
            <option value="service">Services</option>
            <option value="goods">Goods</option>
          </select>
        </div>
        <button className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 border border-primary-200" onClick={loadItems}>Search</button>
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3>{editingItem ? 'Edit' : 'Add'} Catalog Item</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            <input placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              <option value="service">Service</option>
              <option value="goods">Goods</option>
            </select>
            <input placeholder="Unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} />
            <input type="number" placeholder="Price" value={form.unitPrice || ''} onChange={e => setForm({ ...form, unitPrice: parseFloat(e.target.value) || 0 })} />
            <input type="number" placeholder="VAT %" value={form.vatRate || ''} onChange={e => setForm({ ...form, vatRate: parseFloat(e.target.value) || 0 })} />
            <input type="number" placeholder="Income Account" value={form.incomeAccount || ''} onChange={e => setForm({ ...form, incomeAccount: parseInt(e.target.value) || 0 })} />
            <input type="number" placeholder="Expense Account" value={form.expenseAccount || ''} onChange={e => setForm({ ...form, expenseAccount: parseInt(e.target.value) || 0 })} />
            <input type="number" placeholder="VAT Account" value={form.vatAccount || ''} onChange={e => setForm({ ...form, vatAccount: parseInt(e.target.value) || 0 })} />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ width: '100%', marginTop: '0.5rem' }} />
          <button onClick={handleSubmit} style={{ marginTop: '0.5rem' }}>
            {editingItem ? 'Update' : 'Create'}
          </button>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Unit</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              <th style={{ textAlign: 'right' }}>VAT %</th>
              <th>Income Acct</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={item.isObsolete ? { opacity: 0.5 } : {}}>
                <td>{item.code || '-'}</td>
                <td>{item.name}</td>
                <td>{item.type}</td>
                <td>{item.unit}</td>
                <td style={{ textAlign: 'right' }}><MoneyCell value={item.unitPrice} /></td>
                <td style={{ textAlign: 'right' }}>{item.vatRate}%</td>
                <td>{item.incomeAccount}</td>
                <td>
                  <button onClick={() => startEdit(item)}>Edit</button>
                  <button onClick={() => handleDelete(item.id)} style={{ marginLeft: '0.5rem', color: 'red' }}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
